import { createBrowserClient } from "@supabase/ssr";
import type { AuthError, AuthChangeEvent } from "@supabase/supabase-js";

export const TABLES = {
  CONTRACTS: "contracts",
  WORKERS: "workers",
  CUSTOMERS: "customers",
  ADDRESSES: "addresses",
  CONTRACT_TYPES: "contract_types",
  DAILY_REVENUE: "daily_revenue",
  WEEKLY_REVENUE: "weekly_revenue",
  MONTHLY_REVENUE: "monthly_revenue",
  MONTHLY_REVENUE_FORECASTS: "monthly_revenue_forecasts",
  ROLES: "roles",
  ORGANIZATION: "organization",
  PAYROLL: "payroll",
  COMISSIONS: "comissions",
  CONTRACT_CATEGORIES: "contract_categories",
  CONTRACT_COMPANIES: "contract_companies",
  ELECTRICITY_DETAILS: "electricity_contract_details",
  GAS_DETAILS: "gas_contract_details",
  EMAIL_MESSAGES: "email_messages",
  EMAIL_MESSAGE_EVENTS: "email_message_events",
} as const;

// Debug function to help diagnose auth issues
const debugLog = (message: string, data?: unknown) => {
  if (import.meta.env.DEV) {
    console.log(`[Supabase Debug] ${message}`, data || "");
  }
};

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabasePublishableKey =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ??
  import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabasePublishableKey) {
  console.error("Missing Supabase environment variables");
  throw new Error("Missing required environment variables");
}

// Initialize custom storage with debug logging
const customStorage = {
  getItem: (key: string) => {
    debugLog("Getting auth item:", key);
    return localStorage.getItem(key);
  },
  setItem: (key: string, value: string) => {
    debugLog("Setting auth item:", key);
    localStorage.setItem(key, value);
  },
  removeItem: (key: string) => {
    debugLog("Removing auth item:", key);
    localStorage.removeItem(key);
  },
};

// Create and export the Supabase client directly
export const supabase = createBrowserClient(
  supabaseUrl,
  supabasePublishableKey,
  {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: "pkce",
    storage: customStorage,
    debug: import.meta.env.DEV,
  },
  global: {
    headers: {
      "X-Client-Info": "flowbite-react-admin",
    },
  },
}
);

// Set up auth state change listener
supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
  debugLog("Auth state changed:", {
    event,
    userId: session?.user.id,
    timestamp: new Date().toISOString(),
  });
});

// Test connection and log status
(async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error("Supabase connection error:", error.message);
    } else {
      debugLog(
        "Supabase connection successful",
        data.session ? "- Session found" : "- No session"
      );
    }
  } catch (err) {
    console.error("Failed to initialize Supabase:", err);
  }
})();

// Helper function to handle auth errors with more detailed messages
export const handleAuthError = (error: AuthError): string => {
  debugLog("Auth Error:", error);

  switch (error.message) {
    case "Invalid login credentials":
      return "Invalid email or password. Please check your credentials and try again.";
    case "Email not confirmed":
      return "Please verify your email address. Check your inbox for a confirmation link.";
    case "Rate limit exceeded":
      return "Too many attempts. Please wait a few minutes before trying again.";
    case "Network error":
      return "Unable to connect to the authentication service. Please check your internet connection.";
    case "Server error":
      return "Authentication service is temporarily unavailable. Please try again later.";
    default:
      return `Authentication error: ${error.message}`;
  }
};

// Export configuration status
export const getSupabaseStatus = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    return {
      isConfigured: Boolean(supabaseUrl && supabasePublishableKey),
      url: supabaseUrl ? "configured" : "missing",
      publishableKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
        ? "configured"
        : "missing",
      legacyAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY
        ? "configured"
        : "missing",
      hasSession: Boolean(data.session),
      lastChecked: new Date().toISOString(),
    };
  } catch (error) {
    return {
      isConfigured: Boolean(supabaseUrl && supabasePublishableKey),
      url: supabaseUrl ? "configured" : "missing",
      publishableKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
        ? "configured"
        : "missing",
      legacyAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY
        ? "configured"
        : "missing",
      hasSession: false,
      error: error instanceof Error ? error.message : "Unknown error",
      lastChecked: new Date().toISOString(),
    };
  }
};

// Helper function to clear all authentication storage
export const clearAuthStorage = () => {
  try {
    // Clear session storage
    sessionStorage.clear();

    // Clear auth-related localStorage keys
    Object.keys(localStorage).forEach((key) => {
      if (
        key.includes("supabase") ||
        key.includes("auth") ||
        key.startsWith("sb-") ||
        key.includes("session")
      ) {
        localStorage.removeItem(key);
      }
    });

    debugLog("Auth storage cleared successfully");
  } catch (error) {
    console.warn("Error clearing auth storage:", error);
  }
};

export const isSupabaseConfigured = Boolean(
  supabaseUrl && supabasePublishableKey
);
